/* Customer creations. Portrait originals are excluded from every media path. */
(() => {
  if (customElements.get('hf-oct7-reviews')) return;

  class HfOct7Reviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.events = new AbortController();
      this.text = JSON.parse(this.querySelector('[data-oct7-i18n]').textContent);
      this.pageSize = 10;
      this.cases = [];
      this.subjects = [
        ['', this.text.allSubjects], ['Person', this.text.person], ['Pet', this.text.pet],
        ['Two people', this.text.twoPeople], ['Person + Pet', this.text.personPet]
      ];
      this.readState();
      this.configureNavigation();
      this.buildFilters();
      this.bindEvents();
      this.load();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.dialogState = null;
      this.removeAttribute('data-oct7-modal-active');
      this.initialized = false;
    }

    node(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = `hf-oct7-reviews__${className}`;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    button(className, text, action) {
      const button = this.node('button', className, text);
      button.type = 'button';
      if (action) button.dataset.oct7Action = action;
      return button;
    }

    format(key, values) {
      let output = this.text[key] || '';
      for (const [name, value] of Object.entries(values)) output = output.replace(`{${name}}`, String(value));
      return output;
    }

    readState() {
      const params = new URL(window.location.href).searchParams;
      this.subject = this.subjects.some(([value]) => value === params.get('review_subject')) ? params.get('review_subject') : '';
      this.selectedStyle = ['Chibi', 'Realistic'].includes(params.get('review_style')) ? params.get('review_style') : '';
      this.rating = /^[1-5]$/.test(params.get('review_rating') || '') ? Number(params.get('review_rating')) : 0;
      this.page = Math.max(1, Number.parseInt(params.get('review_page'), 10) || 1);
      this.querySelector('[data-oct7-style]').value = this.selectedStyle;
    }

    writeState() {
      if (this.hidden || new URL(location.href).searchParams.get('view') !== 'customer-reviews') return;
      const url = new URL(window.location.href);
      for (const [key, value] of Object.entries({ review_subject: this.subject, review_style: this.selectedStyle, review_rating: this.rating || '', review_sort: '', review_page: this.page > 1 ? this.page : '' })) {
        if (value) url.searchParams.set(key, String(value));
        else url.searchParams.delete(key);
      }
      window.history.replaceState(window.history.state, '', url);
    }

    configureNavigation() {
      const current = new URL(window.location.href);
      const destination = new URL(this.dataset.customizeUrl, window.location.origin);
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem('hf-oct7-return') || 'null'); } catch { saved = null; }
      if (saved?.url) {
        try {
          const candidate = new URL(saved.url, window.location.origin);
          if (candidate.origin === window.location.origin && candidate.pathname.startsWith('/products/')) {
            destination.pathname = candidate.pathname;
            destination.search = candidate.search;
          }
        } catch { /* Keep the known customization route. */ }
      }
      for (const key of ['product', 'type', 'style', 'size', 'preview_theme_id', 'preview_key', 'pb']) {
        if (current.searchParams.has(key)) destination.searchParams.set(key, current.searchParams.get(key));
      }
      const currentTheme = window.Shopify?.theme;
      if (!destination.searchParams.has('preview_theme_id') && currentTheme?.role && currentTheme.role !== 'main' && currentTheme.id) {
        destination.searchParams.set('preview_theme_id', String(currentTheme.id));
      }
      destination.searchParams.delete('view');
      destination.searchParams.delete('step');
      destination.searchParams.set('return_to_customize', '1');
      this.querySelector('[data-oct7-back]').href = destination.toString();
    }

    buildFilters() {
      const makeChoice = (name, value, label) => {
        const wrapper = this.node('label', 'choice');
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = name;
        input.value = value;
        wrapper.append(input, document.createTextNode(label));
        return wrapper;
      };
      const subjects = this.querySelector('[data-oct7-subject-choices]');
      subjects.replaceChildren(...this.subjects.map(([value, label]) => makeChoice('subject', value, label)));
      this.querySelector('[data-oct7-rating-choices]').replaceChildren(makeChoice('rating', '0', this.text.allRatings), ...[5, 4, 3, 2, 1].map(value => makeChoice('rating', String(value), `${value} ${this.text.stars}`)));
    }

    syncFilters() {
      for (const input of this.querySelectorAll('[data-oct7-filter-form] input')) {
        input.checked = input.name === 'subject' ? input.value === this.subject : Number(input.value) === this.rating;
      }
    }

    bindEvents() {
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => {
        const target = event.target.closest('button');
        if (!target || !this.contains(target)) return;
        if (target.hasAttribute('data-oct7-close')) { this.closeDialog(target.closest('dialog')); return; }
        if (target.hasAttribute('data-oct7-retry')) { this.load(); return; }
        if (target.hasAttribute('data-oct7-filter-open')) {
          this.syncFilters();
          this.openDialog(this.querySelector('[data-oct7-filter-dialog]'));
          return;
        }
        if (target.hasAttribute('data-oct7-filter-reset')) {
          const form = this.querySelector('[data-oct7-filter-form]');
          form.elements.subject.value = '';
          form.elements.rating.value = '0';
          return;
        }
        if (target.hasAttribute('data-oct7-clear') || target.dataset.oct7Action === 'clear') {
          this.subject = '';
          this.rating = 0;
          this.selectedStyle = '';
          this.querySelector('[data-oct7-style]').value = '';
          this.page = 1;
          this.render(true);
          return;
        }
        if (target.dataset.oct7Rating) {
          this.rating = this.rating === Number(target.dataset.oct7Rating) ? 0 : Number(target.dataset.oct7Rating);
          this.page = 1;
          this.render(true);
          return;
        }
        if (target.dataset.oct7Action === 'expand') {
          const expanded = target.getAttribute('aria-expanded') !== 'true';
          target.parentElement.querySelector('[data-oct7-review-text]').classList.toggle('hf-oct7-reviews__review-text--collapsed', !expanded);
          target.setAttribute('aria-expanded', String(expanded));
          target.textContent = expanded ? this.text.readLess : this.text.readMore;
          return;
        }
        if (target.dataset.oct7Page) {
          this.page = Number(target.dataset.oct7Page);
          this.render(true);
          return;
        }
        if (target.dataset.oct7Case && target.dataset.oct7Stage) {
          const item = this.cases.find(item => item.id === target.dataset.oct7Case);
          if (item) this.openGallery(item, target.dataset.oct7Stage);
          return;
        }
        if (target.hasAttribute('data-oct7-photo-previous') || target.hasAttribute('data-oct7-photo-next')) {
          const offset = target.hasAttribute('data-oct7-photo-next') ? 1 : -1;
          this.renderGallery('finished', false, this.galleryImageIndex + offset);
          return;
        }
        if (target.dataset.oct7GalleryStage) this.renderGallery(target.dataset.oct7GalleryStage, true);
      }, options);
      this.querySelector('[data-oct7-style]').addEventListener('change', event => {
        this.selectedStyle = event.target.value;
        this.page = 1;
        this.render(true);
      }, options);
      this.querySelector('[data-oct7-filter-form]').addEventListener('submit', event => {
        event.preventDefault();
        const values = new FormData(event.currentTarget);
        this.subject = values.get('subject') || '';
        this.rating = Number(values.get('rating')) || 0;
        this.page = 1;
        this.closeDialog(this.querySelector('[data-oct7-filter-dialog]'), false);
        this.render(true);
      }, options);
      for (const dialog of this.querySelectorAll('dialog')) {
        dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeDialog(dialog); }, options);
        dialog.addEventListener('close', () => this.finishDialog(dialog), options);
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.closeDialog(dialog);
        }, options);
      }
      this.querySelector('[data-oct7-gallery-tabs]').addEventListener('keydown', event => {
        if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const stages = this.galleryStages();
        let index = stages.indexOf(this.galleryStage);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = stages.length - 1;
        else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + stages.length) % stages.length;
        this.renderGallery(stages[index], true);
      }, options);
    }

    openDialog(dialog) {
      const state = { dialog, x: window.scrollX, y: window.scrollY, opener: document.activeElement, restore: true };
      this.dialogState = state;
      this.setAttribute('data-oct7-modal-active', '');
      dialog.showModal();
      this.restoreDialogPosition(state);
    }

    restoreDialogPosition(state) {
      if (!state.restore || this.dialogState !== state) return;
      window.scrollTo({ left: state.x, top: state.y, behavior: 'instant' });
    }

    closeDialog(dialog, restore = true) {
      if (this.dialogState?.dialog === dialog) this.dialogState.restore = restore;
      dialog.close();
      this.finishDialog(dialog);
    }

    finishDialog(dialog) {
      const state = this.dialogState;
      if (!state || state.dialog !== dialog || state.finishing) return;
      state.finishing = true;
      if (state.restore && state.opener?.isConnected) state.opener.focus({ preventScroll: true });
      this.restoreDialogPosition(state);
      requestAnimationFrame(() => {
        this.restoreDialogPosition(state);
        requestAnimationFrame(() => {
          if (this.dialogState !== state) return;
          this.restoreDialogPosition(state);
          this.removeAttribute('data-oct7-modal-active');
          this.dialogState = null;
        });
      });
    }

    async load() {
      this.request?.abort();
      this.request = new AbortController();
      this.setStatus(this.text.loading);
      this.querySelector('[data-oct7-list]').setAttribute('aria-busy', 'true');
      this.querySelector('[data-oct7-retry]').hidden = true;
      try {
        const cache = window.HugfigOct7CaseData ||= new Map();
        if (!cache.has(this.dataset.casesUrl)) {
          const pending = fetch(this.dataset.casesUrl, { credentials: 'omit' }).then(response => {
            if (!response.ok) throw new Error('Could not load examples');
            return response.json();
          }).catch(error => { cache.delete(this.dataset.casesUrl); throw error; });
          cache.set(this.dataset.casesUrl, pending);
        }
        const data = await cache.get(this.dataset.casesUrl);
        if (this.request.signal.aborted) return;
        if (!Array.isArray(data.cases)) throw new Error('Invalid examples');
        this.cases = data.cases.filter(item => item.id && item.images?.finished && item.images?.preview);
        this.summary = data.summary;
        this.render();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setStatus(this.text.error);
        this.querySelector('[data-oct7-retry]').hidden = false;
      } finally {
        this.querySelector('[data-oct7-list]').setAttribute('aria-busy', 'false');
      }
    }

    setStatus(text) {
      this.querySelector('[data-oct7-status]').hidden = !text;
      this.querySelector('[data-oct7-status-text]').textContent = text || '';
    }

    render(scroll = false) {
      const matches = this.cases.filter(item => (!this.subject || item.subject === this.subject) && (!this.selectedStyle || item.style === this.selectedStyle) && (!this.rating || item.rating === this.rating));
      const filtered = this.subject ? matches : this.interleaveSubjects(matches);
      const totalPages = Math.max(1, Math.ceil(filtered.length / this.pageSize));
      this.page = Math.min(this.page, totalPages);
      const start = (this.page - 1) * this.pageSize;
      this.querySelector('[data-oct7-range]').textContent = this.format('range', { start: filtered.length ? start + 1 : 0, end: Math.min(start + this.pageSize, filtered.length), total: filtered.length });
      this.querySelector('[data-oct7-clear]').hidden = !this.selectedStyle && !this.subject && !this.rating;
      const subjectLabel = this.subjects.find(([value]) => value === this.subject)?.[1] || this.text.allSubjects;
      this.querySelector('[data-oct7-filter-label]').textContent = [subjectLabel, this.rating ? `${this.rating} ★` : ''].filter(Boolean).join(' · ');
      this.setStatus(filtered.length ? '' : `${this.text.empty} ${this.text.emptyHint}`);
      this.querySelector('[data-oct7-retry]').hidden = true;
      this.querySelector('[data-oct7-list]').replaceChildren(...filtered.slice(start, start + this.pageSize).map(item => this.card(item)));
      this.renderPagination(filtered.length, totalPages);
      this.renderDistribution();
      this.writeState();
      if (scroll) {
        const heading = this.querySelector('[data-oct7-results-header]');
        heading.focus({ preventScroll: true });
        heading.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }

    interleaveSubjects(items) {
      const pets = items.filter(item => item.subject === 'Pet');
      const people = items.filter(item => item.subject !== 'Pet');
      const ordered = [];
      for (let index = 0; index < Math.max(pets.length, people.length); index += 2) {
        ordered.push(...pets.slice(index, index + 2), ...people.slice(index, index + 2));
      }
      return ordered;
    }

    renderDistribution() {
      const summary = this.querySelector('[data-oct7-summary]');
      summary.hidden = !this.summary;
      if (!this.summary) return;
      this.querySelector('[data-oct7-average]').textContent = Number(this.summary.average).toFixed(2);
      this.querySelector('[data-oct7-average-label]').setAttribute('aria-label', `${this.summary.average} / 5`);
      const rows = [5, 4, 3, 2, 1].map(rating => {
        const row = this.button('rating-row');
        row.dataset.oct7Rating = String(rating);
        row.setAttribute('aria-label', this.format('summaryFilter', { rating }));
        row.setAttribute('aria-pressed', String(this.rating === rating));
        const bar = this.node('span', 'bar');
        bar.setAttribute('aria-hidden', 'true');
        const fill = this.node('span');
        fill.style.width = `${(this.summary.counts[String(rating)] / this.summary.total) * 100}%`;
        bar.append(fill);
        row.append(this.node('span', '', `${rating} ★`), bar, this.node('span', '', String(this.summary.counts[String(rating)])));
        return row;
      });
      this.querySelector('[data-oct7-distribution]').replaceChildren(...rows);
    }

    asset(filename) {
      if (!filename || !/^hf-oct7-case-[a-z0-9-]+\.webp$/i.test(filename)) return '';
      return new URL(filename, new URL(this.dataset.casesUrl, window.location.href)).href;
    }

    finishedImages(item) {
      const filenames = [item?.images?.finished, ...(Array.isArray(item?.finished_images) ? item.finished_images : [])];
      return [...new Set(filenames.map(filename => this.asset(filename)).filter(Boolean))];
    }

    allowedImage(item, stage, imageIndex = 0) {
      if (!item) return '';
      // Subject is the privacy boundary, independent of legacy random visibility flags.
      if (stage === 'original' && String(item.subject || '').trim().toLowerCase() !== 'pet') return '';
      if (stage === 'finished') return this.finishedImages(item)[imageIndex] || '';
      return this.asset(item.images?.[stage]);
    }

    image(item, stage, imageIndex = 0) {
      const image = document.createElement('img');
      image.src = this.allowedImage(item, stage, imageIndex);
      image.alt = item.image_alts?.[stage] || [item.title, this.text[stage]].filter(Boolean).join(' — ');
      image.width = 1200;
      image.height = 1200;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.addEventListener('error', () => { image.alt = `${this.text.error} ${this.text.retry}`; }, { once: true });
      return image;
    }

    media(item, stage, hero = false) {
      const button = this.button('media');
      button.dataset.oct7Case = item.id;
      button.dataset.oct7Stage = stage;
      button.setAttribute('aria-label', this.format('openImage', { stage: this.text[stage] }));
      button.append(this.image(item, stage));
      if (hero) button.append(this.node('span', 'media-label', this.text.finished));
      return button;
    }

    privateTile() {
      const tile = this.node('div', 'private');
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '1.5');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M7 10V7a5 5 0 0 1 10 0v3M5 10h14v11H5zM12 14v3');
      svg.append(path);
      tile.append(svg, this.node('span', '', this.text.private));
      return tile;
    }

    caseMeta(item) {
      const subject = this.subjects.find(([value]) => value === item.subject)?.[1] || item.subject;
      const style = this.text[String(item.style || '').toLowerCase()] || item.style;
      return [subject, style, item.size].filter(Boolean).join(' · ');
    }

    card(item) {
      const card = this.node('article', 'card');
      card.dataset.oct7ReviewId = item.id;
      const meta = this.caseMeta(item);
      card.append(this.node('h2', 'card-meta', meta), this.media(item, 'finished', true));
      const stages = this.node('div', 'stages');
      for (const stage of ['original', 'preview']) {
        const figure = this.node('figure', 'stage');
        const visible = this.allowedImage(item, stage);
        figure.append(visible ? this.media(item, stage) : this.privateTile());
        figure.append(this.node('figcaption', 'caption', this.text[stage]));
        stages.append(figure);
      }
      card.append(stages);
      if (item.review && Number.isInteger(item.rating) && item.rating >= 1 && item.rating <= 5) {
        const review = this.node('div', 'review');
        const stars = this.node('span', 'stars', '★'.repeat(item.rating) + '☆'.repeat(5 - item.rating));
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', `${item.rating} / 5`);
        const copy = this.node('p', 'review-text', item.review);
        copy.classList.add('hf-oct7-reviews__review-text--collapsed');
        copy.dataset.oct7ReviewText = '';
        copy.id = `hf-oct7-review-${item.id}`;
        const expand = this.button('text-button', this.text.readMore, 'expand');
        expand.setAttribute('aria-expanded', 'false');
        expand.setAttribute('aria-controls', copy.id);
        review.append(stars, copy, expand);
        card.append(review);
      }
      return card;
    }

    renderPagination(count, totalPages) {
      const nav = this.querySelector('[data-oct7-pagination]');
      nav.replaceChildren();
      nav.hidden = !count;
      if (!count) return;
      nav.append(this.node('p', 'page-caption', this.format('page', { current: this.page, total: totalPages })));
      const pageButton = (page, label, ariaLabel) => {
        const button = this.button('page-button', label);
        button.dataset.oct7Page = String(page);
        button.setAttribute('aria-label', ariaLabel);
        button.disabled = page < 1 || page > totalPages;
        return button;
      };
      const pages = this.node('div', 'page-links');
      for (let page = 1; page <= totalPages; page += 1) {
        const button = pageButton(page, String(page), this.format('page', { current: page, total: totalPages }));
        if (page === this.page) button.setAttribute('aria-current', 'page');
        pages.append(button);
      }
      nav.append(pages);
      nav.append(pageButton(this.page - 1, '‹', this.text.previous));
      nav.append(pageButton(this.page + 1, '›', this.text.next));
    }

    galleryStages() {
      return ['finished', 'original', 'preview'].filter(stage => this.allowedImage(this.galleryItem, stage));
    }

    openGallery(item, stage) {
      if (!this.allowedImage(item, stage)) return;
      this.galleryItem = item;
      this.querySelector('[data-oct7-gallery-meta]').textContent = this.caseMeta(item);
      this.renderGallery(stage);
      this.openDialog(this.querySelector('[data-oct7-gallery]'));
    }

    renderGallery(stage, focus = false, imageIndex = 0) {
      if (!this.allowedImage(this.galleryItem, stage, imageIndex)) return;
      this.galleryImageIndex = imageIndex;
      this.galleryStage = stage;
      const panel = this.querySelector('[data-oct7-gallery-image]');
      panel.id = 'hf-oct7-gallery-panel';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `hf-oct7-gallery-tab-${stage}`);
      const image = this.image(this.galleryItem, stage, imageIndex);
      image.loading = 'eager';
      panel.replaceChildren(image);
      const photoCount = stage === 'finished' ? this.finishedImages(this.galleryItem).length : 1;
      this.querySelector('[data-oct7-gallery-navigation]').hidden = photoCount <= 1;
      this.querySelector('[data-oct7-photo-count]').textContent = this.format('photoCount', { current: imageIndex + 1, total: photoCount });
      this.querySelector('[data-oct7-photo-previous]').disabled = imageIndex === 0;
      this.querySelector('[data-oct7-photo-next]').disabled = imageIndex >= photoCount - 1;
      const tabs = this.galleryStages().map(name => {
        const tab = this.button('gallery-tab', this.text[name]);
        tab.id = `hf-oct7-gallery-tab-${name}`;
        tab.dataset.oct7GalleryStage = name;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        tab.setAttribute('aria-selected', String(stage === name));
        tab.tabIndex = stage === name ? 0 : -1;
        return tab;
      });
      this.querySelector('[data-oct7-gallery-tabs]').replaceChildren(...tabs);
      if (focus) tabs.find(tab => tab.dataset.oct7GalleryStage === stage)?.focus();
    }
  }

  customElements.define('hf-oct7-reviews', HfOct7Reviews);
})();
